/**
 * Утилитные вспомогательные функции для работы с AMO полями
 * Содержит переиспользуемые функции для устранения дублирования кода
 */

/**
 * Универсальная функция поиска контейнера поля
 * @param {string|number} fieldId - ID поля
 * @returns {Object} {element: HTMLElement|null, success: boolean, error?: string}
 */
export function findFieldContainer(fieldId, { silent = false } = {}) {
    const elements = Array.from(document.querySelectorAll(`[data-id="${fieldId}"]`));
    const fieldControlSelector = [
        `input[name="CFV[${fieldId}]"]`,
        `select[name="CFV[${fieldId}]"]`,
        `textarea[name="CFV[${fieldId}]"]`,
        `input.js-item-checkbox[name^="CFV[${fieldId}]"]`,
    ].join(',');

    const hasFieldControls = (element) => Boolean(element.querySelector(fieldControlSelector));
    const visibleElements = elements.filter(isElementVisible);

    const element = (
        visibleElements.find(hasFieldControls) ||
        elements.find(hasFieldControls) ||
        visibleElements[0] ||
        elements[0] ||
        null
    );

    if (!element) {
        const error = `Контейнер поля ${fieldId} не найден`;
        if (!silent) {
            console.error(error);
        }
        return { element: null, success: false, error };
    }
    return { element, success: true };
}

function isElementVisible(element) {
    let currentElement = element;
    while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
        const style = getComputedStyle(currentElement);
        if (
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            style.visibility === 'collapse'
        ) {
            return false;
        }
        currentElement = currentElement.parentElement;
    }

    return true;
}

/**
 * Универсальная функция для dispatch событий
 * @param {HTMLElement} element - Элемент для отправки события
 * @param {string|Array<string>} eventTypes - Тип события или массив типов
 * @param {Object} options - Опции события
 */
export function dispatchEvents(element, eventTypes, options = { bubbles: true }) {
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    types.forEach(eventType => {
        const event = options.detail 
            ? new CustomEvent(eventType, options)
            : new Event(eventType, options);
        element.dispatchEvent(event);
    });
}

/**
 * Универсальная функция для работы с AMO атрибутами
 * @param {string|number} fieldId - ID поля
 * @returns {Object} {attributes: Object|null, cfvKey: string, success: boolean}
 */
export function getAmoAttributes(fieldId) {
    const attributes = AMOCRM?.data?.current_card?.model?.attributes;
    const cfvKey = `CFV[${fieldId}]`;
    
    if (!attributes) {
        return { attributes: null, cfvKey, success: false };
    }
    
    return { attributes, cfvKey, success: true };
}

/**
 * Универсальная функция для обновления состояния чекбокса
 * @param {HTMLElement} checkbox - Элемент чекбокса
 * @param {boolean} checked - Новое состояние
 * @param {boolean} dispatchEvent - Отправлять ли событие change
 */
export function updateCheckboxState(checkbox, checked, dispatchEvent = true) {
    checkbox.checked = checked;
    
    if (checked) {
        checkbox.setAttribute('checked', 'checked');
    } else {
        checkbox.removeAttribute('checked');
    }
    
    const label = checkbox.closest('label');
    if (label && label.classList.contains('checkboxes_dropdown__label')) {
        if (checked) {
            label.classList.add('is-checked');
        } else {
            label.classList.remove('is-checked');
        }
    }
    
    if (dispatchEvent) {
        dispatchEvents(checkbox, 'change', { bubbles: true, cancelable: true });
    }
}

/**
 * Универсальная функция для обновления мастер-чекбокса
 * @param {HTMLElement} masterCheckbox - Мастер-чекбокс
 * @param {string} state - Состояние: 'none', 'all', 'partial'
 */
export function updateMasterCheckboxState(masterCheckbox, state) {
    const wrapper = masterCheckbox.closest('.js-master-checkbox-wrapper');
    if (!wrapper) return;
    
    const helper = wrapper.querySelector('.control-checkbox__helper');
    
    switch (state) {
        case 'none':
            masterCheckbox.checked = false;
            masterCheckbox.removeAttribute('checked');
            wrapper.classList.remove('is-checked');
            if (helper) helper.classList.remove('control-checkbox__helper_minus');
            break;
            
        case 'all':
            masterCheckbox.checked = true;
            masterCheckbox.setAttribute('checked', 'checked');
            wrapper.classList.add('is-checked');
            if (helper) helper.classList.remove('control-checkbox__helper_minus');
            break;
            
        case 'partial':
            masterCheckbox.checked = true;
            masterCheckbox.setAttribute('checked', 'checked');
            wrapper.classList.add('is-checked');
            if (helper) helper.classList.add('control-checkbox__helper_minus');
            break;
    }
}
